<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" :isClosable="!isSaving && !isUploading">
    <template #content>
      <div
        :style="{
          opacity: isSaving ? '0.5' : '1',
          pointerEvents: isSaving ? 'none' : 'auto',
        }"
      >
        <div class="standard-page-header standard-page-header--small-bottom-padding" style="margin-bottom: 25px">
          <h1 class="global-h1 standard-page-header__title">
            Upload Content for the
            <div class="global-h2" style="margin: 2px 0 9px">{{ props.calendarItemTitle }}</div>
          </h1>
        </div>

        <h5 class="global-h5" style="margin-bottom: 5px">Title</h5>
        <input class="global-text-input" v-model="title" style="max-width: 300px; margin-bottom: 20px" />

        <h5 class="global-h5" style="margin-bottom: 5px">Assets</h5>
        <UploadPhotoForm
          callerName="ContentCalendarUploadContentForItem"
          class=""
          :img="uploadedImages"
          :showClose="false"
          :showRemove="true"
          :autoSelect="false"
          :columnsNumber="{ mobile: 2, tablet: 5, 'tablet-large': 6, laptop: 6, desktop: 6, 'desktop-wide': 6 }[screenSize]"
          :flow="null"
          @imageUploadedToServer="uploadedImage => uploadedImages.push(uploadedImage)"
          @removeMediaFile="
            imgId => {
              uploadedImages.splice(
                uploadedImages.findIndex(i => i.serverId === imgId),
                1
              );
            }
          "
          @uploadProgressChange="info => (isUploading = info.isUploadInProgress)"
          @uploadingStarted="() => (isUploading = true)"
        />
      </div>
    </template>

    <template #footer="{ closeModal }">
      <SrpButton color="gray" fill="outlined" @click="closeModal" :isDisabled="isUploading || isSaving">Cancel</SrpButton>

      <SrpButton
        :isDisabled="isUploading || isSaving || !uploadedImages.length"
        @click="
          async () => {
            await save();
            closeModal();
          }
        "
      >
        <template #icon>
          <LoadingIcon v-if="isSaving" :size="18" />
          <IconEmbedded v-else name="save_2" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Save
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, Ref, inject, onMounted } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import UploadPhotoForm from "@views/CMS/UploadPhotoForm.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Types
import { ContentSummaryData, SocialContentUploadResult } from "@contracts/contentCalendar";
import { UploadedImage } from "@contracts/uploadedImage";
import { ScreenSize } from "@contracts/screenSize";

// Global variables
const screenSize = inject("screenSize") as Ref<ScreenSize>;

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string;
    calendarItemTitle: string;
  }>(),
  {}
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "contentUploaded", contentSummaryData: ContentSummaryData): void;
}>();

// Title ======================================================================
const title = ref<string>("");

onMounted(() => {
  title.value = props.calendarItemTitle;
});

// Uploaded assets ============================================================
const isUploading = ref<boolean>(false);
const uploadedImages = ref<Array<UploadedImage>>([]);

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save() {
  isSaving.value = true;

  const socialContentUploadResult = await ContentCalendarRepo.socialUploadContent(
    props.customerId,
    title.value,
    uploadedImages.value.map(i => i.serverId)
  );

  isSaving.value = false;

  emit("contentUploaded", socialContentUploadResult.contentSummaryData);
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";
</style>
